---
title: "FIFA World Cup 2026 Host Cities"
slug: "fifa-world-cup-2026-host-cities"
excerpt: "A comprehensive guide to the 16 host cities across Canada, Mexico, and the United States for the 2026 FIFA World Cup."
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Sports"
tags:
  - tournament
  - host cities
  - fifa
  - soccer
  - football
featuredImage: "https://footballtoday.com/wp-content/uploads/2022/06/IMG_8081.jpg"
status: "published"
draft: false
---

## Short Answer

<ShortAnswer>
The **2026 FIFA World Cup** will be hosted by three countries—**Canada, Mexico, and the United States**—across **16 cities**. This marks the first time the tournament will be hosted by multiple nations. The host cities include **Toronto, Vancouver, Mexico City, Guadalajara, Los Angeles, Dallas, Atlanta, and others**, with matches spread across stadiums with a combined capacity of over 1.5 million spectators.
</ShortAnswer>

## Infobox

<InfoBox title="2026 FIFA World Cup">
<ul>
<li><strong>Host countries:</strong> Canada, Mexico, United States</li>
<li><strong>Host cities:</strong> 16</li>
<li><strong>Dates:</strong> June–July 2026</li>
<li><strong>Teams:</strong> 48</li>
<li><strong>Matches:</strong> 104</li>
<li><strong>Venues:</strong> 16 stadiums</li>
<li><strong>Top scorer:</strong> TBD</li>
<li><strong>Champion:</strong> TBD</li>
</ul>
</InfoBox>

## Overview

The **2026 FIFA World Cup** is scheduled to be the **23rd edition** of the quadrennial international men's football championship organized by **FIFA**. For the first time in its history, the tournament will be hosted by **three countries**—**Canada, Mexico, and the United States**—spanning **16 host cities**. This expansion from the traditional 32-team format to **48 teams** will make it the largest World Cup in history, with **104 matches** played across the continent.

The tournament is set to take place from **June to July 2026**, with the final expected to be held at the **MetLife Stadium in East Rutherford, New Jersey, USA**. The host nations were selected by FIFA in June 2018, beating out a competing bid from Morocco. The decision to expand the tournament and award it to a joint bid was part of FIFA's strategic plan to grow the sport globally and increase accessibility.

### Host Countries and Cities

The 2026 World Cup will be hosted across three nations, each contributing a set of host cities:

- **Canada:** Toronto (BMO Field), Vancouver (BC Place)
- **Mexico:** Mexico City (Estadio Azteca), Guadalajara (Estadio Akron), Monterrey (Estadio BBVA)
- **United States:** Atlanta (Mercedes-Benz Stadium), Boston (Gillette Stadium), Dallas (AT&T Stadium), Houston (NRG Stadium), Kansas City (Arrowhead Stadium), Los Angeles (SoFi Stadium), Miami (Hard Rock Stadium), New York/New Jersey (MetLife Stadium), Philadelphia (Lincoln Financial Field), San Francisco Bay Area (Levi's Stadium), Seattle (Lumen Field)

Each stadium has been selected based on infrastructure, capacity, and readiness to host FIFA World Cup matches. Several venues, such as **SoFi Stadium (Los Angeles)** and **AT&T Stadium (Dallas)**, are among the largest in the world, capable of seating over 90,000 spectators.

## History / Background

The concept of a **multi-nation World Cup** was first seriously considered during FIFA's **2026 bid process**, which began in 2017. Prior to this, all World Cups had been hosted by a single country, with the exception of the **2002 tournament**, which was co-hosted by South Korea and Japan. The expansion to 48 teams was approved by FIFA in January 2017, aiming to include more nations and grow the global fanbase.

The joint bid by Canada, Mexico, and the United States was officially announced in April 2017. It was seen as a strategic move to unite North America under a single World Cup bid, leveraging existing infrastructure, strong football cultures, and economic synergies. The bid was formally submitted to FIFA in August 2017 and was selected as the host on **June 13, 2018**, during FIFA's Congress in Moscow, defeating Morocco's bid in a 134–65 vote.

This decision marked a significant shift in FIFA's hosting strategy, emphasizing **continental unity** and **sustainability**. The 2026 World Cup will also be the first to feature a **unified qualification process** across all three host nations, with automatic berths reserved for the host countries.

## How It Works

### Format and Qualification

The 2026 FIFA World Cup will feature **48 teams**, an increase from the previous 32-team format. The expanded format includes:

- **16 groups of three teams each** (48 teams total)
- **Top two teams from each group advance** to a **32-team knockout stage**
- **Additional knockout rounds** including Round of 32, Round of 16, Quarter-finals, Semi-finals, and Final

Each host nation automatically qualifies, while the remaining 45 spots will be determined through **continental qualification tournaments** organized by FIFA's six confederations. The allocation of spots is as follows:

- **UEFA (Europe):** 16 spots
- **CAF (Africa):** 9 spots
- **AFC (Asia):** 8 spots
- **CONCACAF (North & Central America & Caribbean):** 6 spots (+ 3 hosts)
- **CONMEBOL (South America):** 6 spots
- **OFC (Oceania):** 1 spot

### Venue and Logistics

The tournament will utilize **16 stadiums** across the three host countries, with each venue required to meet FIFA's stringent standards for capacity, safety, and broadcast facilities. The stadiums range from modern multi-purpose venues like **SoFi Stadium (Los Angeles)** to historic grounds such as **Estadio Azteca (Mexico City)**, which hosted two World Cup finals (1970 and 1986).

Logistical planning includes:

- **Transportation networks** linking host cities via air, rail, and road
- **Fan zones and cultural events** in major cities
- **Security and emergency response** plans in coordination with local authorities
- **Sustainability initiatives**, including green stadium certifications and carbon offset programs

## Important Facts

<FactList>
<ul>
<li><strong>First multi-nation World Cup:</strong> The 2026 tournament is the first to be hosted by three countries.</li>
<li><strong>Largest World Cup ever:</strong> With 48 teams and 104 matches, it surpasses all previous editions in scale.</li>
<li><strong>Automatic qualification for hosts:</strong> Canada, Mexico, and the USA automatically qualify as hosts.</li>
<li><strong>New stadiums and renovations:</strong> Several venues, including AT&T Stadium (Dallas) and Mercedes-Benz Stadium (Atlanta), have undergone major upgrades.</li>
<li><strong>Increased global reach:</strong> The expanded format aims to include more nations from underrepresented regions.</li>
<li><strong>Legacy projects:</strong> Host cities are investing in youth football development and infrastructure improvements.</li>
<li><strong>Broadcasting rights:</strong> The tournament will be broadcast in over 200 territories, reaching billions of viewers worldwide.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "April 2017", title: "Joint bid announced", description: "Canada, Mexico, and the United States announce their joint bid." },
  { date: "June 2017", title: "Expansion approved", description: "FIFA approves the expansion of the tournament format to 48 teams." },
  { date: "August 2017", title: "Bid submitted", description: "The official bid dossier is submitted to FIFA for review." },
  { date: "June 13, 2018", title: "Host selected", description: "FIFA selects the North American joint bid over Morocco's bid." },
  { date: "2019–2025", title: "Venue preparations", description: "Stadium construction and renovation projects are completed across host cities." },
  { date: "2023–2025", title: "Global qualification", description: "Qualification tournaments are held globally to determine the 45 remaining spots." },
  { date: "June–July 2026", title: "Tournament begins", description: "The 2026 FIFA World Cup takes place across North America." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li><strong>FIFA World Cup</strong> – The premier international football competition.</li>
<li><strong>Host nation</strong> – A country selected to organize a major tournament.</li>
<li><strong>Qualification</strong> – The process by which national teams earn berths in the tournament.</li>
<li><strong>Knockout stage</strong> – The single-elimination phase of the tournament starting after the group stage.</li>
<li><strong>FIFA Congress</strong> – The supreme legislative body of FIFA, responsible for hosting decisions.</li>
<li><strong>CONCACAF</strong> – The governing body of football in North and Central America and the Caribbean.</li>
<li><strong>Expansion format</strong> – The increase in the number of participating teams in a tournament.</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>How many teams will participate in the 2026 World Cup?</summary>
<p>The 2026 FIFA World Cup will feature <strong>48 teams</strong>, an increase from the previous 32-team format.</p>
</details>
<details>
<summary>Which countries are hosting the 2026 World Cup?</summary>
<p>The tournament will be hosted by <strong>Canada, Mexico, and the United States</strong>.</p>
</details>
<details>
<summary>How many host cities are there?</summary>
<p>There are <strong>16 host cities</strong> across the three countries.</p>
</details>
<details>
<summary>Will the host nations automatically qualify?</summary>
<p>Yes, <strong>Canada, Mexico, and the USA</strong> automatically qualify as hosts.</p>
</details>
<details>
<summary>When and where will the final be played?</summary>
<p>The final is scheduled for <strong>July 2026</strong> at <strong>MetLife Stadium in East Rutherford, New Jersey, USA</strong>.</p>
</details>
<details>
<summary>How many matches will be played in total?</summary>
<p>A total of <strong>104 matches</strong> will be played during the tournament.</p>
</details>
<details>
<summary>What is the format of the tournament?</summary>
<p>The tournament begins with <strong>16 groups of three teams</strong>. The top two from each group advance to a <strong>32-team knockout stage</strong>.</p>
</details>
<details>
<summary>Which stadiums will host matches?</summary>
<p>Matches will be held in 16 stadiums, including <strong>SoFi Stadium (Los Angeles), AT&T Stadium (Dallas), and Estadio Azteca (Mexico City)</strong>.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>FIFA official tournament portal for the 2026 World Cup.</li>
<li>FIFA Member Associations directories and profiles.</li>
<li>CONCACAF official website and confederation news.</li>
<li>FIFA official congress documents and host selection criteria.</li>
<li>Sports Reference historical World Cup soccer database.</li>
</ol>
</ReferenceList>
